import { useEffect, useMemo, useState } from 'react';
import QuestionStats from './components/QuestionStats';
import questions from './data/questions.json';
import {
  getQuestionStats,
  registerAnswer,
  resetAllStats,
  toggleQuestionValidation,
  useQuestionValidation,
} from './utils/quizStats';
import './App.css';

const BOOKMARKS_KEY = 'ope-labo-test-bookmarks-v1';
const SESSION_COUNT = 10;

const sourceOptions = [
  { id: 'all', label: 'Todos' },
  ...Array.from(
    new Map(
      questions.map((q) => [q.sourcePdf, { id: q.sourcePdf, label: q.sourceLabel }])
    ).values()
  ),
];

function getStoredBookmarks() {
  try {
    const value = window.localStorage.getItem(BOOKMARKS_KEY);
    const parsed = value ? JSON.parse(value) : [];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function pickRandom(pool, count) {
  return [...pool].sort(() => Math.random() - 0.5).slice(0, Math.min(count, pool.length));
}

function pickWeighted(pool, count) {
  const items = pool.map((q) => {
    const attempts = getQuestionStats(q.id);
    const last5 = attempts.slice(-5);
    const failures = last5.filter((a) => !a.correcto).length;
    return { q, weight: Math.pow(2, failures) };
  });

  const result = [];
  const available = [...items];
  const actualCount = Math.min(count, pool.length);

  for (let i = 0; i < actualCount; i++) {
    const totalWeight = available.reduce((sum, item) => sum + item.weight, 0);
    let rand = Math.random() * totalWeight;
    let idx = available.length - 1;
    for (let j = 0; j < available.length; j++) {
      rand -= available[j].weight;
      if (rand <= 0) { idx = j; break; }
    }
    result.push(available[idx].q);
    available.splice(idx, 1);
  }

  return result;
}

function OsakidetzaLogo({ size = 26 }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 32 32"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
    >
      <rect width="32" height="32" rx="6" fill="rgba(255,255,255,0.18)" />
      <rect x="13" y="5" width="6" height="22" rx="3" fill="white" />
      <rect x="5" y="13" width="22" height="6" rx="3" fill="white" />
    </svg>
  );
}

function ValidateButton({ questionId }) {
  const validated = useQuestionValidation(questionId);
  return (
    <button
      type="button"
      className={`action-btn validate-btn${validated ? ' active' : ''}`}
      onClick={() => toggleQuestionValidation(questionId)}
      title={
        validated
          ? 'Validada contra PDF · pulsa para desmarcar'
          : 'Marcar como validada contra el PDF original'
      }
    >
      {validated ? 'v PDF' : 'Validar'}
    </button>
  );
}

function App() {
  const [mode, setMode] = useState('all');

  // "all" mode
  const [sourceFilter, setSourceFilter] = useState('all');
  const [onlyBookmarked, setOnlyBookmarked] = useState(false);
  const [currentQuestionId, setCurrentQuestionId] = useState(questions[0]?.id ?? '');
  const [bookmarkedIds, setBookmarkedIds] = useState(() => getStoredBookmarks());

  // Session (random modes)
  const [sessionQuestions, setSessionQuestions] = useState([]);
  const [sessionIndex, setSessionIndex] = useState(0);
  const [sessionDone, setSessionDone] = useState(false);

  // Answers (in-memory, resets on new session)
  const [selectedAnswers, setSelectedAnswers] = useState({});

  // Settings modal
  const [showSettings, setShowSettings] = useState(false);
  const [resetKeepValidations, setResetKeepValidations] = useState(true);
  const [resetDone, setResetDone] = useState(false);

  useEffect(() => {
    window.localStorage.setItem(BOOKMARKS_KEY, JSON.stringify(bookmarkedIds));
  }, [bookmarkedIds]);

  const bookmarkedSet = useMemo(() => new Set(bookmarkedIds), [bookmarkedIds]);

  const filteredQuestions = useMemo(
    () =>
      questions.filter((q) => {
        const matchesSource = sourceFilter === 'all' || q.sourcePdf === sourceFilter;
        const matchesBookmark = !onlyBookmarked || bookmarkedSet.has(q.id);
        return matchesSource && matchesBookmark;
      }),
    [bookmarkedSet, onlyBookmarked, sourceFilter]
  );

  useEffect(() => {
    if (!filteredQuestions.length) { setCurrentQuestionId(''); return; }
    const stillVisible = filteredQuestions.some((q) => q.id === currentQuestionId);
    if (!stillVisible) setCurrentQuestionId(filteredQuestions[0].id);
  }, [currentQuestionId, filteredQuestions]);

  const currentIndex =
    mode === 'all'
      ? Math.max(0, filteredQuestions.findIndex((q) => q.id === currentQuestionId))
      : sessionIndex;

  const questionList = mode === 'all' ? filteredQuestions : sessionQuestions;
  const currentQuestion = questionList[currentIndex] ?? null;

  const selectedOption = currentQuestion ? selectedAnswers[currentQuestion.id] : null;
  const isAnswered = Boolean(selectedOption);
  const hasCorrectOption = Boolean(currentQuestion?.correctOption);
  const isAlternative = currentQuestion?.answerMode === 'alternative';
  const isExplanationOnly = currentQuestion?.answerMode === 'explanation_only';
  const isBookmarked = currentQuestion ? bookmarkedSet.has(currentQuestion.id) : false;

  function goTo(index) {
    if (mode === 'all') {
      const q = filteredQuestions[index];
      if (q) setCurrentQuestionId(q.id);
    } else {
      if (index >= sessionQuestions.length) {
        setSessionDone(true);
      } else if (index >= 0) {
        setSessionIndex(index);
      }
    }
  }

  function toggleBookmark(id) {
    setBookmarkedIds((prev) =>
      prev.includes(id) ? prev.filter((b) => b !== id) : [...prev, id]
    );
  }

  function selectAnswer(optionId) {
    if (!currentQuestion || selectedAnswers[currentQuestion.id]) return;
    registerAnswer(currentQuestion.id, optionId === currentQuestion.correctOption);
    setSelectedAnswers((prev) => ({ ...prev, [currentQuestion.id]: optionId }));
  }

  function startSession(modeType) {
    const picked =
      modeType === 'random10'
        ? pickRandom(questions, SESSION_COUNT)
        : pickWeighted(questions, SESSION_COUNT);
    setSessionQuestions(picked);
    setSessionIndex(0);
    setSessionDone(false);
    setSelectedAnswers({});
  }

  function changeMode(newMode) {
    setMode(newMode);
    setSelectedAnswers({});
    setSessionQuestions([]);
    setSessionIndex(0);
    setSessionDone(false);
  }

  function handleReset() {
    resetAllStats(resetKeepValidations);
    setSelectedAnswers({});
    setSessionQuestions([]);
    setSessionDone(false);
    setResetDone(true);
    setTimeout(() => setResetDone(false), 3000);
  }

  const sessionResults = useMemo(() => {
    if (!sessionDone) return null;
    return sessionQuestions.map((q) => ({
      q,
      answer: selectedAnswers[q.id] ?? null,
      correct: selectedAnswers[q.id] != null && selectedAnswers[q.id] === q.correctOption,
      skipped: !selectedAnswers[q.id],
    }));
  }, [sessionDone, sessionQuestions, selectedAnswers]);

  const sessionScore = sessionResults ? sessionResults.filter((r) => r.correct).length : 0;

  const isLastSessionQuestion =
    mode !== 'all' && sessionQuestions.length > 0 && currentIndex === sessionQuestions.length - 1;

  return (
    <div className="app">
      {/* Header */}
      <header className="app-header">
        <div className="header-brand">
          <OsakidetzaLogo size={26} />
          <span className="header-title">OPE Labo</span>
        </div>
        <button
          type="button"
          className="header-settings-btn"
          onClick={() => setShowSettings(true)}
          aria-label="Ajustes"
        >
          ⚙
        </button>
      </header>

      {/* Mode tabs */}
      <nav className="mode-nav" aria-label="Modo de práctica">
        <button
          type="button"
          className={mode === 'all' ? 'mode-tab active' : 'mode-tab'}
          onClick={() => changeMode('all')}
        >
          Todas en orden
        </button>
        <button
          type="button"
          className={mode === 'random10' ? 'mode-tab active' : 'mode-tab'}
          onClick={() => changeMode('random10')}
        >
          10 aleatorias
        </button>
        <button
          type="button"
          className={mode === 'weighted10' ? 'mode-tab active' : 'mode-tab'}
          onClick={() => changeMode('weighted10')}
        >
          10 por fallos
        </button>
      </nav>

      <main className="app-main">
        {/* "All" mode: filters */}
        {mode === 'all' && (
          <div className="filter-panel">
            <div className="filter-header">
              <label className="bookmark-toggle">
                <input
                  type="checkbox"
                  checked={onlyBookmarked}
                  onChange={(e) => setOnlyBookmarked(e.target.checked)}
                />
                <span>Solo guardadas</span>
              </label>
              <span className="filter-count">{filteredQuestions.length} preguntas</span>
            </div>
            <div className="chip-row">
              {sourceOptions.map((opt) => (
                <button
                  key={opt.id}
                  type="button"
                  className={opt.id === sourceFilter ? 'chip active' : 'chip'}
                  onClick={() => setSourceFilter(opt.id)}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Random mode: session launcher */}
        {mode !== 'all' && !sessionDone && sessionQuestions.length === 0 && (
          <div className="session-launch">
            <h2>
              {mode === 'random10' ? '10 preguntas aleatorias' : '10 preguntas ponderadas por fallos'}
            </h2>
            <p>
              {mode === 'weighted10'
                ? 'Las preguntas con más fallos en los últimos 5 intentos tienen más probabilidad de aparecer (cada fallo dobla su peso).'
                : 'Se eligen 10 preguntas al azar del banco completo de preguntas.'}
            </p>
            <button type="button" className="primary-button" onClick={() => startSession(mode)}>
              Empezar sesión
            </button>
          </div>
        )}

        {/* Session results */}
        {mode !== 'all' && sessionDone && sessionResults && (
          <div className="session-results">
            <div className="results-header">
              <h2>Sesión completada</h2>
              <div className="results-score">
                <span className="score-number">{sessionScore}</span>
                <span className="score-total"> / {sessionQuestions.length}</span>
              </div>
            </div>
            <div className="results-list">
              {sessionResults.map((r, i) => (
                <div
                  key={r.q.id}
                  className={`result-item ${r.skipped ? 'skipped' : r.correct ? 'correct' : 'wrong'}`}
                >
                  <span className="result-num">{i + 1}</span>
                  <span className="result-text">
                    {r.q.prompt.length > 80 ? r.q.prompt.slice(0, 80) + '…' : r.q.prompt}
                  </span>
                  <span className="result-icon">
                    {r.skipped ? '–' : r.correct ? '✓' : '✗'}
                  </span>
                </div>
              ))}
            </div>
            <div className="results-actions">
              <button type="button" className="primary-button" onClick={() => startSession(mode)}>
                Nueva sesión
              </button>
              <button type="button" className="ghost-button" onClick={() => changeMode('all')}>
                Ver todas
              </button>
            </div>
          </div>
        )}

        {/* Question */}
        {currentQuestion && !sessionDone && (
          <div className="question-view">
            <div className="question-nav">
              <button
                type="button"
                className="nav-btn"
                onClick={() => goTo(currentIndex - 1)}
                disabled={currentIndex === 0}
              >
                ‹ Anterior
              </button>
              <span className="nav-position">
                {currentIndex + 1} / {questionList.length}
              </span>
              <button
                type="button"
                className="nav-btn"
                onClick={() => goTo(currentIndex + 1)}
                disabled={mode === 'all' && currentIndex === questionList.length - 1}
              >
                {isLastSessionQuestion ? 'Finalizar ›' : 'Siguiente ›'}
              </button>
            </div>

            <article className="question-card">
              <div className="question-meta">
                <span className="tag">#{currentQuestion.questionNumber}</span>
                <span className="tag tag-source">{currentQuestion.sourceLabel}</span>
                {isAlternative && (
                  <span className="tag tag-warning">Resp. alternativa</span>
                )}
                {isExplanationOnly && (
                  <span className="tag tag-neutral">Sin resp. cerrada</span>
                )}
                <div className="question-actions">
                  <ValidateButton questionId={currentQuestion.id} />
                  {mode === 'all' && (
                    <button
                      type="button"
                      className={`action-btn${isBookmarked ? ' active' : ''}`}
                      onClick={() => toggleBookmark(currentQuestion.id)}
                    >
                      {isBookmarked ? '★' : '☆'}
                    </button>
                  )}
                </div>
              </div>

              <h2 className="question-text">{currentQuestion.prompt}</h2>

              <QuestionStats questionId={currentQuestion.id} />

              <div className="answers-list">
                {currentQuestion.options.map((option) => {
                  const isSelected = selectedOption === option.id;
                  const isCorrect = currentQuestion.correctOption === option.id;

                  let stateClass = '';
                  if (isAnswered && hasCorrectOption) {
                    if (isCorrect) stateClass = 'correct';
                    else if (isSelected) stateClass = 'wrong';
                  }

                  return (
                    <button
                      key={option.id}
                      type="button"
                      className={`answer-btn${isSelected ? ' selected' : ''}${stateClass ? ' ' + stateClass : ''}`}
                      onClick={() => selectAnswer(option.id)}
                      disabled={isAnswered}
                    >
                      <span className="answer-letter">{option.id.toUpperCase()}</span>
                      <span className="answer-text">{option.text}</span>
                    </button>
                  );
                })}
              </div>

              {isAnswered && (
                <div className="feedback" aria-live="polite">
                  {isExplanationOnly ? (
                    <p>El documento no fija una opción correcta cerrada para esta pregunta.</p>
                  ) : selectedOption === currentQuestion.correctOption ? (
                    <p>
                      {isAlternative ? 'Propuesta alternativa correcta: ' : 'Correcto: '}
                      <strong>{currentQuestion.correctOption.toUpperCase()}</strong>
                    </p>
                  ) : (
                    <p>
                      Respondiste <strong>{selectedOption.toUpperCase()}</strong>.
                      {isAlternative ? ' Propuesta alternativa: ' : ' Correcta: '}
                      <strong>{currentQuestion.correctOption.toUpperCase()}</strong>
                    </p>
                  )}
                  {currentQuestion.feedbackNote && <p>{currentQuestion.feedbackNote}</p>}
                </div>
              )}
            </article>
          </div>
        )}

        {/* Empty state */}
        {mode === 'all' && !currentQuestion && (
          <div className="empty-state">
            <p>No hay preguntas en esta vista. Cambia el filtro.</p>
          </div>
        )}
      </main>

      {/* Settings modal */}
      {showSettings && (
        <div className="modal-overlay" onClick={() => setShowSettings(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Ajustes</h2>
              <button
                type="button"
                className="modal-close"
                onClick={() => setShowSettings(false)}
              >
                ✕
              </button>
            </div>
            <div className="modal-body">
              <section className="settings-section">
                <h3>Restablecer datos</h3>
                <p>
                  Borra todos los registros de aciertos y fallos guardados en el navegador.
                </p>
                <label className="settings-checkbox">
                  <input
                    type="checkbox"
                    checked={resetKeepValidations}
                    onChange={(e) => setResetKeepValidations(e.target.checked)}
                  />
                  <span>Conservar preguntas validadas contra PDF</span>
                </label>
                <button type="button" className="danger-button" onClick={handleReset}>
                  Borrar estadísticas
                </button>
                {resetDone && (
                  <p className="reset-confirmation">Datos restablecidos correctamente.</p>
                )}
              </section>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default App;
